// ─────────────────────────────────────────────────────────────────────────
// Trabajadores — ficha base de RRHH (26-ago-2026).
//
// La FICHA de cada trabajador: identificación, contrato y los campos
// previsionales que piden las liquidaciones de sueldo chilenas (AFP, salud,
// gratificación, cargas, seguro de cesantía). Las liquidaciones y el control
// de vacaciones se construyen ENCIMA de esta ficha (placeholders abajo).
// Nunca se borra un trabajador: se DESVINCULA con fecha y motivo.
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import backend from "../../backend";
import useTitle from "../../hooks/useTitle";
import useIsMobile from "../../hooks/useIsMobile";
import AusenciasCard from "./AusenciasCard";
import AsistenciaCard from "./AsistenciaCard";
import DocumentosCard from "./DocumentosCard";
import ConfirmModal from "../../components/design-system/ConfirmModal";
import { formatPrice, maskRut, maskPhone } from "../../utils";

const AFPS = ["Capital", "Cuprum", "Habitat", "Modelo", "Planvital", "Provida", "Uno"];

const AREA = {
  taller:         { label: "Taller",         color: "#3498db", bg: "#0d2030" },
  administracion: { label: "Administración", color: "#9b59b6", bg: "#241530" },
};
const GRATIF_LABEL = {
  art50_tope: "Art. 50 (25% con tope 4,75 IMM)",
  art47:      "Art. 47 (30% utilidades)",
  sin:        "Sin gratificación",
};

const fmt = n => `$${formatPrice(Math.round(n || 0))}`;
const num = { fontVariantNumeric: "tabular-nums" };
const S = {
  card:  { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: 14 },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" },
  input: { background: "#161616", border: "1px solid #333", borderRadius: 8, padding: "10px 12px", fontSize: 14, color: "#ddd", width: "100%", boxSizing: "border-box" },
  seccion: { fontSize: 11, fontWeight: 800, color: "#e30613", textTransform: "uppercase", letterSpacing: ".06em", margin: "16px 0 8px" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 10 },
  // Tabla desktop (pulido 27-ago): la lista en pantalla ancha es tabla, no tarjetas
  th: {
    background: "#1f1f1f", padding: "8px 12px", textAlign: "left", fontSize: 10,
    fontWeight: 700, color: "#999", borderBottom: "1px solid #2e2e2e",
    textTransform: "uppercase", letterSpacing: ".04em", whiteSpace: "nowrap",
  },
  td: {
    padding: "8px 12px", color: "#bbb", borderBottom: "1px solid #252525",
    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
  },
};

// Fecha ISO → valor de <input type="date">
const aFechaInput = v => (v ? String(v).slice(0, 10) : "");

// Antigüedad legible desde fechaIngreso (hasta la desvinculación si aplica)
const antiguedad = t => {
  if (!t.fechaIngreso) return null;
  const desde = new Date(t.fechaIngreso);
  const hasta = t.estado === "desvinculado" && t.fechaDesvinculacion ? new Date(t.fechaDesvinculacion) : new Date();
  let meses = (hasta.getFullYear() - desde.getFullYear()) * 12 + (hasta.getMonth() - desde.getMonth());
  if (hasta.getDate() < desde.getDate()) meses -= 1;
  if (meses < 0) meses = 0;
  const a = Math.floor(meses / 12), m = meses % 12;
  if (a === 0 && m === 0) return "menos de 1 mes";
  const partes = [];
  if (a > 0) partes.push(`${a} ${a === 1 ? "año" : "años"}`);
  if (m > 0) partes.push(`${m} ${m === 1 ? "mes" : "meses"}`);
  return partes.join(" ");
};

const FICHA_VACIA = {
  nombres: "", apellidos: "", rut: "", fechaNacimiento: "", direccion: "", comuna: "",
  telefono: "", email: "", cargo: "", area: "taller", fechaIngreso: "",
  tipoContrato: "indefinido", fechaTerminoContrato: "", jornada: "44 hrs L-V",
  sueldoBase: "", gratificacion: "art50_tope", afp: "", salud: "fonasa",
  isapreNombre: "", planUf: "", cargasFamiliares: "0", seguroCesantia: true,
  colacion: "", movilizacion: "", bonosFijos: [],
  banco: "", bancoTipo: "", bancoNumero: "", notas: "",
};

const Trabajadores = () => {
  useTitle("Trabajadores");
  // Pulido desktop 27-ago: móvil intacto (tarjetas); en desktop la lista es
  // tabla y la ficha usa el ancho (form + columna lateral en >1100px).
  const isMobile = useIsMobile();
  const angosto = useIsMobile(1100); // ficha a 1 columna bajo 1100px

  const [rows, setRows] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [aviso, setAviso] = useState(null);
  const [verDesvinculados, setVerDesvinculados] = useState(false);
  // Filtro por área (28-ago): chips Taller/Administración con contador (toggle)
  const [filtroArea, setFiltroArea] = useState(null); // null = todas

  // Desvinculación con motivo (28-ago): ConfirmModal en vez de confirm+prompt
  const [desvTarget, setDesvTarget] = useState(null);
  const [desvMotivo, setDesvMotivo] = useState("");

  // null = lista · "nuevo" = alta · objeto = editando esa ficha
  const [editando, setEditando] = useState(null);
  const [f, setF] = useState(FICHA_VACIA);
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      const r = await backend.get("trabajador");
      setRows(r.data || []);
    } catch (e) { console.error(e); }
    setCargando(false);
  };
  useEffect(() => { cargar(); }, []);

  const avisar = (tipo, texto) => { setAviso({ tipo, texto }); setTimeout(() => setAviso(null), 4000); };
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const abrirNuevo = () => { setF(FICHA_VACIA); setEditando("nuevo"); window.scrollTo(0, 0); };
  const abrirFicha = t => {
    setF({
      nombres: t.nombres || "", apellidos: t.apellidos || "", rut: t.rut || "",
      fechaNacimiento: aFechaInput(t.fechaNacimiento), direccion: t.direccion || "",
      comuna: t.comuna || "", telefono: t.telefono || "", email: t.email || "",
      cargo: t.cargo || "", area: t.area || "taller", fechaIngreso: aFechaInput(t.fechaIngreso),
      tipoContrato: t.tipoContrato || "indefinido",
      fechaTerminoContrato: aFechaInput(t.fechaTerminoContrato),
      jornada: t.jornada || "", sueldoBase: t.sueldoBase ? String(t.sueldoBase) : "",
      gratificacion: t.gratificacion || "art50_tope", afp: t.afp || "",
      salud: t.salud || "fonasa", isapreNombre: t.isapreNombre || "",
      planUf: t.planUf ? String(t.planUf) : "", cargasFamiliares: String(t.cargasFamiliares ?? 0),
      seguroCesantia: t.seguroCesantia !== false,
      colacion: t.colacion ? String(t.colacion) : "", movilizacion: t.movilizacion ? String(t.movilizacion) : "",
      bonosFijos: (t.bonosFijos || []).map(b => ({ glosa: b.glosa || "", monto: b.monto ? String(b.monto) : "", imponible: b.imponible !== false })),
      banco: t.cuentaBanco?.banco || "", bancoTipo: t.cuentaBanco?.tipo || "",
      bancoNumero: t.cuentaBanco?.numero || "", notas: t.notas || "",
    });
    setEditando(t);
    window.scrollTo(0, 0);
  };

  const guardar = async () => {
    if (!f.nombres.trim() || !f.apellidos.trim()) { avisar("error", "Nombres y apellidos son obligatorios"); return; }
    if (!f.rut.trim()) { avisar("error", "Falta el RUT"); return; }
    setGuardando(true);
    const body = {
      nombres: f.nombres.trim(), apellidos: f.apellidos.trim(), rut: f.rut.trim(),
      fechaNacimiento: f.fechaNacimiento || null, direccion: f.direccion.trim(),
      comuna: f.comuna.trim(), telefono: f.telefono.trim(), email: f.email.trim(),
      cargo: f.cargo.trim(), area: f.area, fechaIngreso: f.fechaIngreso || null,
      tipoContrato: f.tipoContrato,
      fechaTerminoContrato: f.tipoContrato === "indefinido" ? null : (f.fechaTerminoContrato || null),
      jornada: f.jornada.trim(), sueldoBase: Math.round(Number(f.sueldoBase) || 0),
      gratificacion: f.gratificacion, afp: f.afp, salud: f.salud,
      isapreNombre: f.salud === "isapre" ? f.isapreNombre.trim() : "",
      planUf: f.salud === "isapre" ? Number(f.planUf) || 0 : 0,
      cargasFamiliares: Math.max(0, Math.round(Number(f.cargasFamiliares) || 0)),
      seguroCesantia: !!f.seguroCesantia,
      colacion: Math.max(0, Math.round(Number(f.colacion) || 0)),
      movilizacion: Math.max(0, Math.round(Number(f.movilizacion) || 0)),
      bonosFijos: (f.bonosFijos || [])
        .map(b => ({ glosa: (b.glosa || "").trim() || "Bono", monto: Math.max(0, Math.round(Number(b.monto) || 0)), imponible: b.imponible !== false }))
        .filter(b => b.monto > 0),
      cuentaBanco: { banco: f.banco.trim(), tipo: f.bancoTipo.trim(), numero: f.bancoNumero.trim() },
      notas: f.notas.trim(),
    };
    try {
      if (editando === "nuevo") {
        await backend.post("trabajador", body);
        avisar("ok", `Ficha de ${body.nombres} creada`);
      } else {
        await backend.patch(`trabajador/${editando._id}`, body);
        avisar("ok", "Ficha actualizada");
      }
      setEditando(null);
      cargar();
    } catch (e) {
      avisar("error", e?.response?.data?.error || e.message);
    }
    setGuardando(false);
  };

  const confirmarDesvincular = async () => {
    const t = desvTarget;
    if (!t) return;
    const nombre = `${t.nombres} ${t.apellidos}`;
    try {
      await backend.patch(`trabajador/${t._id}/desvincular`, { fecha: new Date().toISOString(), motivo: desvMotivo.trim() });
      avisar("ok", `${nombre} desvinculado`);
      setDesvTarget(null);
      setDesvMotivo("");
      setEditando(null);
      cargar();
    } catch (e) { avisar("error", e?.response?.data?.error || e.message); }
  };

  const activos = rows.filter(t => t.estado === "activo");
  const desvinculados = rows.filter(t => t.estado === "desvinculado");
  const base = verDesvinculados ? rows : activos;
  const visibles = filtroArea ? base.filter(t => (t.area || "taller") === filtroArea) : base;
  const nPorArea = k => base.filter(t => (t.area || "taller") === k).length;

  const campo = (label, contenido) => (
    <div>
      <div style={{ ...S.label, marginBottom: 4 }}>{label}</div>
      {contenido}
    </div>
  );

  // ── Formulario (alta / edición) ──────────────────────────────────────
  if (editando) {
    const esNuevo = editando === "nuevo";
    const desv = !esNuevo && editando.estado === "desvinculado";
    // Desktop ancho: formulario + columna lateral (ausencias/asistencia/liquidaciones)
    const dosColumnas = !esNuevo && !angosto;
    return (
      <div style={{ padding: "12px 12px 24px", background: "#1a1a1a", minHeight: "100%", color: "#ddd", maxWidth: isMobile ? 760 : (esNuevo ? 940 : 1280) }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: "#fff" }}>
            {esNuevo ? "🧑‍🏭 Nuevo trabajador" : `🧑‍🏭 ${editando.nombres} ${editando.apellidos}`}
          </div>
          {aviso && (
            <span style={{ fontSize: 12, fontWeight: 700, color: aviso.tipo === "ok" ? "#2ecc71" : "#e74c3c" }}>
              {aviso.tipo === "ok" ? "✓ " : "⚠ "}{aviso.texto}
            </span>
          )}
        </div>

        {desv && (
          <div style={{ ...S.card, borderColor: "#4a2020", marginBottom: 12, fontSize: 12, color: "#e74c3c" }}>
            Desvinculado el {new Date(editando.fechaDesvinculacion).toLocaleDateString("es-CL")}
            {editando.motivoDesvinculacion ? ` — ${editando.motivoDesvinculacion}` : ""}. La ficha se conserva de solo lectura histórica.
          </div>
        )}

        <div style={dosColumnas
          ? { display: "grid", gridTemplateColumns: "minmax(0, 1fr) 380px", gap: 14, alignItems: "start" }
          : undefined}>
        <div style={{ ...S.card, marginBottom: dosColumnas ? 0 : 14 }}>
          {/* ── Identificación ── */}
          <div style={{ ...S.seccion, marginTop: 0 }}>Identificación</div>
          <div style={S.grid}>
            {campo("Nombres *", <input style={S.input} value={f.nombres} onChange={e => set("nombres", e.target.value)} placeholder="Juan Andrés" />)}
            {campo("Apellidos *", <input style={S.input} value={f.apellidos} onChange={e => set("apellidos", e.target.value)} placeholder="Pérez Soto" />)}
            {campo("RUT *", <input style={{ ...S.input, ...num }} value={f.rut} onChange={e => set("rut", maskRut(e.target.value))} placeholder="12.345.678-9" />)}
            {campo("Fecha de nacimiento", <input type="date" style={S.input} value={f.fechaNacimiento} onChange={e => set("fechaNacimiento", e.target.value)} />)}
            {campo("Teléfono", <input style={S.input} value={f.telefono} onChange={e => set("telefono", maskPhone(e.target.value))} placeholder="+56 9 …" />)}
            {campo("Email", <input type="email" style={S.input} value={f.email} onChange={e => set("email", e.target.value)} placeholder="correo@…" />)}
            {campo("Dirección", <input style={S.input} value={f.direccion} onChange={e => set("direccion", e.target.value)} />)}
            {campo("Comuna", <input style={S.input} value={f.comuna} onChange={e => set("comuna", e.target.value)} placeholder="Quilicura" />)}
          </div>

          {/* ── Contrato ── */}
          <div style={S.seccion}>Contrato</div>
          <div style={S.grid}>
            {campo("Cargo", <input style={S.input} value={f.cargo} onChange={e => set("cargo", e.target.value)} placeholder="Maestro tornero" />)}
            {campo("Área", (
              <select style={S.input} value={f.area} onChange={e => set("area", e.target.value)}>
                <option value="taller">Taller</option>
                <option value="administracion">Administración</option>
              </select>
            ))}
            {campo("Fecha de ingreso", <input type="date" style={S.input} value={f.fechaIngreso} onChange={e => set("fechaIngreso", e.target.value)} />)}
            {campo("Tipo de contrato", (
              <select style={S.input} value={f.tipoContrato} onChange={e => set("tipoContrato", e.target.value)}>
                <option value="indefinido">Indefinido</option>
                <option value="plazo_fijo">Plazo fijo</option>
                <option value="por_obra">Por obra o faena</option>
              </select>
            ))}
            {f.tipoContrato !== "indefinido" &&
              campo("Término del contrato", <input type="date" style={S.input} value={f.fechaTerminoContrato} onChange={e => set("fechaTerminoContrato", e.target.value)} />)}
            {campo("Jornada", <input style={S.input} value={f.jornada} onChange={e => set("jornada", e.target.value)} placeholder="44 hrs L-V" />)}
          </div>

          {/* ── Remuneración y previsión ── */}
          <div style={S.seccion}>Remuneración y previsión</div>
          <div style={S.grid}>
            {campo("Sueldo base (CLP)", <input type="number" inputMode="numeric" min={0} style={{ ...S.input, ...num }} value={f.sueldoBase} onChange={e => set("sueldoBase", e.target.value)} placeholder="600000" />)}
            {campo("Gratificación", (
              <select style={S.input} value={f.gratificacion} onChange={e => set("gratificacion", e.target.value)}>
                <option value="art50_tope">{GRATIF_LABEL.art50_tope}</option>
                <option value="art47">{GRATIF_LABEL.art47}</option>
                <option value="sin">{GRATIF_LABEL.sin}</option>
              </select>
            ))}
            {campo("AFP", (
              <select style={S.input} value={f.afp} onChange={e => set("afp", e.target.value)}>
                <option value="">— Sin definir —</option>
                {AFPS.map(a => <option key={a} value={a}>{a}</option>)}
              </select>
            ))}
            {campo("Salud", (
              <select style={S.input} value={f.salud} onChange={e => set("salud", e.target.value)}>
                <option value="fonasa">Fonasa (7%)</option>
                <option value="isapre">Isapre</option>
              </select>
            ))}
            {f.salud === "isapre" && campo("Isapre", <input style={S.input} value={f.isapreNombre} onChange={e => set("isapreNombre", e.target.value)} placeholder="Banmédica, Colmena…" />)}
            {f.salud === "isapre" && campo("Plan (UF)", <input type="number" step="0.01" min={0} style={{ ...S.input, ...num }} value={f.planUf} onChange={e => set("planUf", e.target.value)} placeholder="3.5" />)}
            {campo("Cargas familiares", <input type="number" inputMode="numeric" min={0} style={{ ...S.input, ...num }} value={f.cargasFamiliares} onChange={e => set("cargasFamiliares", e.target.value)} />)}
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, cursor: "pointer", fontSize: 13, color: f.seguroCesantia ? "#2ecc71" : "#b8b8b8" }}>
            <input type="checkbox" checked={f.seguroCesantia} onChange={e => set("seguroCesantia", e.target.checked)}
              style={{ width: 20, height: 20, accentColor: "#2ecc71" }} />
            Seguro de cesantía AFC (obligatorio para contratos desde oct-2002)
          </label>

          {/* ── Haberes fijos del mes (28-sep, pedido del contador) ── */}
          <div style={S.seccion}>Haberes fijos del mes</div>
          <div style={{ fontSize: 12, color: "#b8b8b8", marginBottom: 8 }}>
            Montos que se repiten todos los meses. La liquidación los toma de acá; no hay que digitarlos cada vez.
          </div>
          <div style={S.grid}>
            {campo("Colación (CLP, no imponible)", <input type="number" inputMode="numeric" min={0} style={{ ...S.input, ...num }} value={f.colacion} onChange={e => set("colacion", e.target.value)} placeholder="0" />)}
            {campo("Movilización (CLP, no imponible)", <input type="number" inputMode="numeric" min={0} style={{ ...S.input, ...num }} value={f.movilizacion} onChange={e => set("movilizacion", e.target.value)} placeholder="0" />)}
          </div>
          {(f.bonosFijos || []).map((b, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap", alignItems: "center" }}>
              <input style={{ ...S.input, flex: "2 1 180px" }} value={b.glosa} placeholder="Nombre del bono"
                onChange={e => set("bonosFijos", f.bonosFijos.map((x, j) => (j === i ? { ...x, glosa: e.target.value } : x)))} />
              <input type="number" inputMode="numeric" min={0} style={{ ...S.input, ...num, flex: "1 1 110px" }} value={b.monto} placeholder="Monto"
                onChange={e => set("bonosFijos", f.bonosFijos.map((x, j) => (j === i ? { ...x, monto: e.target.value } : x)))} />
              <select style={{ ...S.input, flex: "1 1 130px" }} value={b.imponible ? "si" : "no"}
                onChange={e => set("bonosFijos", f.bonosFijos.map((x, j) => (j === i ? { ...x, imponible: e.target.value === "si" } : x)))}>
                <option value="si">Imponible</option>
                <option value="no">No imponible</option>
              </select>
              <button type="button" className="t-02 tap-card" title="Quitar bono"
                onClick={() => set("bonosFijos", f.bonosFijos.filter((_, j) => j !== i))}
                style={{ background: "transparent", border: "1px solid #555", color: "#b8b8b8", borderRadius: 8, padding: "8px 12px", cursor: "pointer" }}>
                Quitar
              </button>
            </div>
          ))}
          <button type="button" className="t-02 tap-card"
            onClick={() => set("bonosFijos", [...(f.bonosFijos || []), { glosa: "", monto: "", imponible: true }])}
            style={{ marginTop: 10, background: "transparent", border: "1px dashed #555", color: "#b8b8b8", borderRadius: 8, padding: "8px 12px", cursor: "pointer", fontSize: 13 }}>
            + Agregar bono fijo
          </button>

          {/* ── Pago ── */}
          <div style={S.seccion}>Cuenta para el pago</div>
          <div style={S.grid}>
            {campo("Banco", <input style={S.input} value={f.banco} onChange={e => set("banco", e.target.value)} placeholder="BancoEstado" />)}
            {campo("Tipo de cuenta", <input style={S.input} value={f.bancoTipo} onChange={e => set("bancoTipo", e.target.value)} placeholder="CuentaRUT / corriente / vista" />)}
            {campo("N° de cuenta", <input style={{ ...S.input, ...num }} value={f.bancoNumero} onChange={e => set("bancoNumero", e.target.value)} />)}
          </div>

          <div style={{ ...S.seccion }}>Notas</div>
          <textarea style={{ ...S.input, minHeight: 60, resize: "vertical" }} value={f.notas}
            onChange={e => set("notas", e.target.value)} placeholder="Observaciones internas (talla de EPP, contacto de emergencia…)" />

          {/* ── Acciones ── */}
          <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap", alignItems: "center" }}>
            <button onClick={guardar} disabled={guardando} className="t-02 tap-card"
              style={{ minHeight: 46, borderRadius: 10, border: "none", background: guardando ? "#333" : "#e30613", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", padding: "0 26px" }}>
              {guardando ? "Guardando…" : esNuevo ? "Crear ficha" : "Guardar cambios"}
            </button>
            <button onClick={() => setEditando(null)} className="t-02"
              style={{ minHeight: 46, borderRadius: 10, border: "1px solid #333", background: "#1e1e1e", color: "#b8b8b8", fontSize: 13, cursor: "pointer", padding: "0 18px" }}>
              Volver
            </button>
            {!esNuevo && !desv && (
              <button onClick={() => { setDesvTarget(editando); setDesvMotivo(""); }} className="t-02"
                style={{ minHeight: 46, borderRadius: 10, border: "1px solid #4a2020", background: "#2d1515", color: "#e74c3c", fontSize: 13, fontWeight: 700, cursor: "pointer", padding: "0 18px", marginLeft: "auto" }}>
                Desvincular…
              </button>
            )}
          </div>
        </div>

        {/* ── Módulos sobre la ficha (27-ago): ausencias + asistencia ── */}
        {!esNuevo && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <AusenciasCard trabajadorId={editando._id} trabajador={editando} />
            <AsistenciaCard trabajadorId={editando._id} />
            <DocumentosCard trabajadorId={editando._id} />
            <div style={{ ...S.card, opacity: 0.6 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>🧾 Liquidaciones de sueldo</div>
              <div style={{ fontSize: 11, color: "#8a8a8a", marginTop: 4 }}>
                Se generan por período en la pantalla Liquidaciones (menú) usando estos datos: sueldo base, gratificación, AFP, salud, cargas, AFC.
              </div>
            </div>
          </div>
        )}
        </div>

        {/* Modal desvincular con motivo (28-ago, reemplaza confirm+prompt) */}
        <ConfirmModal
          open={!!desvTarget}
          title={`Desvincular a ${desvTarget?.nombres || ""} ${desvTarget?.apellidos || ""}`}
          description={`${desvTarget?.cargo || "Sin cargo"} · ${desvTarget?.rut || "—"}`}
          warning="La ficha no se borra: se conserva con su historia laboral (liquidaciones, asistencia, documentos)."
          confirmLabel="Desvincular"
          confirmVariant="danger"
          onConfirm={confirmarDesvincular}
          onCancel={() => { setDesvTarget(null); setDesvMotivo(""); }}
        >
          <div className="bini-field" style={{ marginBottom: 12 }}>
            <label>Motivo de la desvinculación</label>
            <textarea
              className="bini-input"
              rows={2}
              value={desvMotivo}
              onChange={e => setDesvMotivo(e.target.value)}
              placeholder="Renuncia, necesidades de la empresa, término de obra…"
              style={{ resize: "vertical", minHeight: 44 }}
              autoFocus
            />
          </div>
        </ConfirmModal>
      </div>
    );
  }

  // ── Lista ────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: "12px 12px 24px", background: "#1a1a1a", minHeight: "100%", color: "#ddd", maxWidth: isMobile ? 760 : 1200 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: "#fff" }}>🧑‍🏭 Trabajadores</div>
        {aviso && (
          <span style={{ fontSize: 12, fontWeight: 700, color: aviso.tipo === "ok" ? "#2ecc71" : "#e74c3c" }}>
            {aviso.tipo === "ok" ? "✓ " : "⚠ "}{aviso.texto}
          </span>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <button onClick={abrirNuevo} className="t-02 tap-card"
          style={{ minHeight: 44, borderRadius: 10, border: "none", background: "#e30613", color: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", padding: "0 20px" }}>
          + Nuevo trabajador
        </button>
        <span style={{ fontSize: 12, color: "#8a8a8a", ...num }}>
          {activos.length} {activos.length === 1 ? "activo" : "activos"}
        </span>
        {/* Chips por área (28-ago): filtran la lista; el badge Área también */}
        {["taller", "administracion"].map(k => {
          const a = AREA[k];
          const activo = filtroArea === k;
          return (
            <button key={k} onClick={() => setFiltroArea(prev => prev === k ? null : k)}
              className="chip-touch no-touch-min t-02"
              title={activo ? "Quitar el filtro de área" : `Filtrar: solo ${a.label}`}
              style={{
                background: activo ? a.bg : "#1e1e1e",
                color: activo ? a.color : "#999",
                border: `1px solid ${activo ? a.color : "#333"}`,
                borderRadius: 8, padding: "7px 12px", fontSize: 12,
                fontWeight: activo ? 700 : 400, cursor: "pointer", ...num,
              }}>
              {a.label} ({nPorArea(k)}){activo ? " ✕" : ""}
            </button>
          );
        })}
        {desvinculados.length > 0 && (
          <button onClick={() => setVerDesvinculados(v => !v)} className="chip-touch no-touch-min t-02"
            style={{
              background: verDesvinculados ? "#3a1114" : "#1e1e1e",
              color: verDesvinculados ? "#ff8a8f" : "#999",
              border: `1px solid ${verDesvinculados ? "#e30613" : "#333"}`,
              borderRadius: 8, padding: "7px 12px", fontSize: 12, cursor: "pointer",
            }}>
            {verDesvinculados ? "Ocultar desvinculados" : `Ver desvinculados (${desvinculados.length})`}
          </button>
        )}
      </div>

      {cargando ? (
        <div style={{ color: "#8a8a8a", fontSize: 12, padding: 16 }}>Cargando…</div>
      ) : visibles.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", color: "#8a8a8a", fontSize: 12 }}>
          {filtroArea
            ? `Sin trabajadores en ${AREA[filtroArea]?.label || filtroArea} con este filtro.`
            : 'Sin trabajadores registrados. Parta con "+ Nuevo trabajador".'}
        </div>
      ) : !isMobile ? (
        /* ── Desktop: tabla ── */
        <div style={{ border: "1px solid #2e2e2e", borderRadius: 8, overflowX: "auto", background: "#232323" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, tableLayout: "fixed" }}>
            <colgroup>
              <col />                            {/* Nombre */}
              <col style={{ width: "110px" }} /> {/* RUT */}
              <col style={{ width: "160px" }} /> {/* Cargo */}
              <col style={{ width: "120px" }} /> {/* Área */}
              <col style={{ width: "110px" }} /> {/* Antigüedad */}
              <col style={{ width: "170px" }} /> {/* AFP / Salud */}
              <col style={{ width: "110px" }} /> {/* Sueldo */}
              <col style={{ width: "110px" }} /> {/* Estado */}
            </colgroup>
            <thead>
              <tr>
                <th style={S.th}>Nombre</th>
                <th style={S.th}>RUT</th>
                <th style={S.th}>Cargo</th>
                <th style={S.th}>Área</th>
                <th style={S.th}>Antigüedad</th>
                <th style={S.th}>AFP / Salud</th>
                <th style={{ ...S.th, textAlign: "right" }}>Sueldo base</th>
                <th style={S.th}>Estado</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((t, i) => {
                const a = AREA[t.area] || AREA.taller;
                const ant = antiguedad(t);
                const desv = t.estado === "desvinculado";
                return (
                  <tr key={t._id} onClick={() => abrirFicha(t)} className="bini-row-hover"
                    style={{ cursor: "pointer", background: i % 2 === 0 ? "#232323" : "#212121", opacity: desv ? 0.55 : 1 }}>
                    <td style={{ ...S.td, color: "#fff", fontWeight: 700 }}>{t.nombres} {t.apellidos}</td>
                    <td style={{ ...S.td, ...num }}>{t.rut}</td>
                    <td style={S.td}>{t.cargo || "—"}</td>
                    <td style={{ ...S.td, overflow: "visible" }}>
                      <span
                        onClick={e => { e.stopPropagation(); setFiltroArea(prev => prev === (t.area || "taller") ? null : (t.area || "taller")); }}
                        title={`Filtrar: solo ${a.label}`}
                        style={{ fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 20, color: a.color, background: a.bg, border: `1px solid ${a.color}33`, cursor: "pointer" }}>
                        {a.label}
                      </span>
                    </td>
                    <td style={{ ...S.td, color: "#8a8a8a" }}>{ant || "—"}</td>
                    <td style={{ ...S.td, fontSize: 11 }}>
                      {t.afp ? `AFP ${t.afp}` : "AFP s/definir"} · {t.salud === "isapre" ? (t.isapreNombre ? `Isapre ${t.isapreNombre}` : "Isapre") : "Fonasa"}
                    </td>
                    <td style={{ ...S.td, textAlign: "right", color: "#fff", fontWeight: 700, ...num }}>
                      {t.sueldoBase > 0 ? fmt(t.sueldoBase) : "—"}
                    </td>
                    <td style={{ ...S.td, fontSize: 11, fontWeight: 700, color: desv ? "#e74c3c" : "#2ecc71" }}>
                      {desv ? "Desvinculado" : "Activo"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {visibles.map(t => {
            const a = AREA[t.area] || AREA.taller;
            const ant = antiguedad(t);
            const desv = t.estado === "desvinculado";
            return (
              <div key={t._id} onClick={() => abrirFicha(t)} className="tap-card t-02"
                style={{ ...S.card, padding: "12px 14px", cursor: "pointer", opacity: desv ? 0.55 : 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>
                      {t.nombres} {t.apellidos}
                      {desv && <span style={{ fontSize: 10, color: "#e74c3c", fontWeight: 700 }}> · DESVINCULADO</span>}
                    </div>
                    <div style={{ fontSize: 11, color: "#b8b8b8", marginTop: 2 }}>
                      {t.cargo || "Sin cargo"} · <span style={num}>{t.rut}</span>
                      {ant && <span style={{ color: "#8a8a8a" }}> · {ant}</span>}
                    </div>
                    <div style={{ fontSize: 11, color: "#8a8a8a", marginTop: 2 }}>
                      {t.afp ? `AFP ${t.afp}` : "AFP sin definir"} · {t.salud === "isapre" ? (t.isapreNombre ? `Isapre ${t.isapreNombre}` : "Isapre") : "Fonasa"}
                      {t.cargasFamiliares > 0 && ` · ${t.cargasFamiliares} ${t.cargasFamiliares === 1 ? "carga" : "cargas"}`}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "#fff", ...num }}>
                      {t.sueldoBase > 0 ? fmt(t.sueldoBase) : "—"}
                    </div>
                    <span
                      onClick={e => { e.stopPropagation(); setFiltroArea(prev => prev === (t.area || "taller") ? null : (t.area || "taller")); }}
                      title={`Filtrar: solo ${a.label}`}
                      style={{ fontSize: 10, fontWeight: 700, padding: "1px 8px", borderRadius: 20, color: a.color, background: a.bg, border: `1px solid ${a.color}33`, cursor: "pointer" }}>
                      {a.label}
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Trabajadores;
